<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AtualizacaoIndex from '../laisAtualizacao/pages/AtualizacaoIndex.vue';
import CaptacaoIndex from '../laisCaptacao/pages/CaptacaoIndex.vue';

const route = useRoute();
const { t } = useI18n();
const prateleiraLabel = computed(() => t('PRATELEIRA.ARIA_LABEL'));

const CAPTACAO_COMPONENT_ROUTE_NAMES = new Set([
  'lais_prateleira_captacao',
  'lais_prateleira_importacao',
  'lais_prateleira_publicacao',
  'lais_prateleira_integridade',
]);

const sharedQueryKeys = [
  'tab',
  'fonte',
  'source',
  'sourceKind',
  'source_kind',
  'origem',
  'periodo',
  'period',
  'desde',
  'ate',
  'from',
  'to',
  'updatedSince',
  'updatedUntil',
  'status',
  'responsavel',
  'responsavel_id',
  'responsavelId',
  'assignee',
  'assigneeId',
  'usuario_email',
  'userEmail',
  'run_id',
  'runId',
  'review_id',
  'reviewId',
  'publication_status',
  'publicationStatus',
  'imovel_id',
  'imovelId',
  'owner_relationship_id',
  'ownerRelationshipId',
  'contact_id',
  'contactId',
  'capture_opportunity_id',
  'captureOpportunityId',
];

const liveTabs = computed(() => [
  {
    key: 'atualizacao',
    label: t('PRATELEIRA.TABS.ATUALIZACAO.LABEL'),
    helper: t('PRATELEIRA.TABS.ATUALIZACAO.HELPER'),
    icon: 'i-lucide-refresh-ccw',
    routeName: 'lais_prateleira_atualizacao',
    query: {},
  },
  {
    key: 'captacao',
    label: t('PRATELEIRA.TABS.CAPTACAO.LABEL'),
    helper: t('PRATELEIRA.TABS.CAPTACAO.HELPER'),
    icon: 'i-lucide-house-plus',
    routeName: 'lais_prateleira_captacao',
    query: {},
  },
  {
    key: 'importacao',
    label: t('PRATELEIRA.TABS.IMPORTACAO.LABEL'),
    helper: t('PRATELEIRA.TABS.IMPORTACAO.HELPER'),
    icon: 'i-lucide-upload-cloud',
    routeName: 'lais_prateleira_importacao',
    query: { workspace: 'importacao' },
  },
  {
    key: 'publicacao',
    label: t('PRATELEIRA.TABS.PUBLICACAO.LABEL'),
    helper: t('PRATELEIRA.TABS.PUBLICACAO.HELPER'),
    icon: 'i-lucide-radio-tower',
    routeName: 'lais_prateleira_publicacao',
    query: { workspace: 'publicacao' },
  },
  {
    key: 'integridade',
    label: t('PRATELEIRA.TABS.INTEGRIDADE.LABEL'),
    helper: t('PRATELEIRA.TABS.INTEGRIDADE.HELPER'),
    icon: 'i-lucide-shield-check',
    routeName: 'lais_prateleira_integridade',
    query: { workspace: 'integridade' },
  },
]);

const activeComponent = computed(() =>
  CAPTACAO_COMPONENT_ROUTE_NAMES.has(route.name)
    ? CaptacaoIndex
    : AtualizacaoIndex
);

const activeTabKey = computed(
  () =>
    liveTabs.value.find(tab => tab.routeName === route.name)?.key || 'captacao'
);

const sharedQuery = computed(() =>
  sharedQueryKeys.reduce((query, key) => {
    if (route.query[key]) query[key] = route.query[key];
    return query;
  }, {})
);

const tabRoute = tab => ({
  name: tab.routeName,
  params: route.params,
  query: {
    ...sharedQuery.value,
    ...tab.query,
  },
});
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-[#f7f8fb] font-redHatText">
    <nav
      class="shrink-0 border-b border-lais-border bg-white px-4 py-3 sm:px-6 lg:px-10"
      :aria-label="prateleiraLabel"
    >
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:flex">
        <RouterLink
          v-for="tab in liveTabs"
          :key="tab.key"
          :to="tabRoute(tab)"
          class="group flex min-w-0 items-center gap-3 rounded-lais border px-3 py-2 text-left transition-colors xl:min-w-[176px]"
          :class="
            activeTabKey === tab.key
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
          "
          :aria-current="activeTabKey === tab.key ? 'page' : undefined"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-white shadow-lais-soft"
          >
            <span class="size-4" :class="tab.icon" aria-hidden="true" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[13px] font-semibold">
              {{ tab.label }}
            </span>
            <span class="block truncate text-[11px] opacity-75">
              {{ tab.helper }}
            </span>
          </span>
        </RouterLink>
      </div>
    </nav>
    <div class="min-h-0 flex-1">
      <component :is="activeComponent" />
    </div>
  </div>
</template>
